import { baseApi } from './baseApi';
import {
  ClienteFiscal,
  CreateClienteFiscalRequest,
  UpdateClienteFiscalRequest,
  ParseQRDataRequest,
  ParsedClienteDataDto,
  ProductoFacturacion,
  CreateProductoFacturacionRequest,
  UpdateProductoFacturacionRequest,
  SolicitarFacturaRequest,
  FacturaResult,
  TimbrarFacturaResult,
  CancelarFacturaRequest,
  CancelarFacturaResult,
  FacturaTicket,
  ReexpedicionResultado,
  FacturaListItem,
  FacturaFilter,
  FacturaDetalle,
  PagedResult,
  CatalogoUsoCFDI,
  CatalogoRegimenFiscal,
  CatalogoFormaPago,
  CatalogoMetodoPago,
  CatalogoProductoServicio,
  CatalogoUnidadMedida,
  RestaurantFacturacionConfig,
  CreateFacturacionConfigRequest,
  UpdateFacturacionConfigRequest,
  ActivateFacturacionRequest,
  FacturacionActivationResult,
  CertificadoInfo,
  SubirCertificadoRequest,
  SubirCertificadoResult,
  ActivarCertificadoResult,
  RevocarCertificadoResult,
  EnviarFacturaEmailRequest,
  EnviarFacturaEmailResult,
  RestaurantEmailConfig,
  CreateEmailConfigRequest,
  UpdateEmailConfigRequest,
  TestEmailConfigRequest,
  TestEmailConfigResult,
  OrdenesSinFacturarResult,
  OrdenesSinFacturarFilter,
  FacturarOrdenesMasivoRequest,
  FacturarOrdenesMasivoResult,
  TimbrarFacturasMasivoRequest,
  TimbrarFacturasMasivoResult,
  RegenerarFacturasMasivoRequest,
  RegenerarFacturasMasivoResult,
  SifeiEnvironment,
  TimbresCompartidos,
  OrganizacionByRfc,
  VincularPorRfcRequest,
  VincularPorRfcResult,
  CrearFacturaSinOrdenRequest,
} from '../types/facturacion';

export const facturacionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ============================================================================
    // Clientes Fiscales
    // ============================================================================

    getClientesFiscales: builder.query<ClienteFiscal[], void>({
      query: () => '/facturacion/clientes',
      providesTags: ['ClienteFiscal'],
    }),

    getClienteFiscalByRFC: builder.query<ClienteFiscal, string>({
      query: (rfc) => `/facturacion/clientes/rfc/${rfc}`,
      providesTags: (_result, _error, rfc) => [{ type: 'ClienteFiscal', id: rfc }],
    }),

    getClienteFiscalById: builder.query<ClienteFiscal, string>({
      query: (id) => `/facturacion/clientes/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'ClienteFiscal', id }],
    }),

    createClienteFiscal: builder.mutation<ClienteFiscal, CreateClienteFiscalRequest>({
      query: (newCliente) => ({
        url: '/facturacion/clientes',
        method: 'POST',
        body: newCliente,
      }),
      invalidatesTags: ['ClienteFiscal'],
    }),

    updateClienteFiscal: builder.mutation<ClienteFiscal, { id: string } & UpdateClienteFiscalRequest>({
      query: ({ id, ...patch }) => ({
        url: `/facturacion/clientes/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: ['ClienteFiscal'],
    }),

    deleteClienteFiscal: builder.mutation<void, string>({
      query: (id) => ({
        url: `/facturacion/clientes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ClienteFiscal'],
    }),

    refrescarClientesFiscales: builder.mutation<{ message: string; count: number; clientes: ClienteFiscal[] }, void>({
      query: () => ({
        url: '/facturacion/clientes/refrescar',
        method: 'POST',
      }),
      invalidatesTags: ['ClienteFiscal'],
    }),

    importClientesFiscales: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/facturacion/clientes/import',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ClienteFiscal'],
    }),

    parseQRCliente: builder.mutation<ParsedClienteDataDto, ParseQRDataRequest>({
      query: (data) => ({
        url: '/facturacion/clientes/parse-qr',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [],
    }),

    // ============================================================================
    // Productos Facturacion
    // ============================================================================

    getProductosFacturacion: builder.query<ProductoFacturacion[], void>({
      query: () => '/facturacion/productos',
      providesTags: ['ProductoFacturacion'],
    }),

    getProductoFacturacionByProductId: builder.query<ProductoFacturacion, string>({
      query: (productId) => `/facturacion/productos/product/${productId}`,
      providesTags: (_result, _error, productId) => [{ type: 'ProductoFacturacion', id: productId }],
    }),

    createProductoFacturacion: builder.mutation<ProductoFacturacion, CreateProductoFacturacionRequest>({
      query: (newProducto) => ({
        url: '/facturacion/productos',
        method: 'POST',
        body: newProducto,
      }),
      invalidatesTags: ['ProductoFacturacion'],
    }),

    updateProductoFacturacion: builder.mutation<ProductoFacturacion, { id: string } & UpdateProductoFacturacionRequest>({
      query: ({ id, ...patch }) => ({
        url: `/facturacion/productos/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: ['ProductoFacturacion'],
    }),

    deleteProductoFacturacion: builder.mutation<void, string>({
      query: (id) => ({
        url: `/facturacion/productos/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ProductoFacturacion'],
    }),

    syncProductoFacturador: builder.mutation<ProductoFacturacion, string>({
      query: (productId) => ({
        url: `/facturacion/productos/${productId}/sync`,
        method: 'POST',
      }),
      invalidatesTags: ['ProductoFacturacion'],
    }),

    // ============================================================================
    // Facturacion
    // ============================================================================

    getFacturas: builder.query<PagedResult<FacturaListItem>, FacturaFilter>({
      query: (filter) => {
        const params = new URLSearchParams();
        if (filter.estado) params.append('estado', filter.estado);
        if (filter.serie) params.append('serie', filter.serie);
        if (filter.fechaInicio) params.append('fechaInicio', filter.fechaInicio);
        if (filter.fechaFin) params.append('fechaFin', filter.fechaFin);
        if (filter.searchTerm) params.append('searchTerm', filter.searchTerm);
        if (filter.origen) params.append('origen', filter.origen);
        if (filter.pageNumber) params.append('pageNumber', filter.pageNumber.toString());
        if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());

        return `/facturacion/facturas?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ facturaId }) => ({ type: 'Factura' as const, id: facturaId })),
              { type: 'Factura', id: 'LIST' },
            ]
          : [{ type: 'Factura', id: 'LIST' }],
    }),

    getFacturaDetalle: builder.query<FacturaDetalle, number>({
      query: (facturaId) => `/facturacion/facturas/${facturaId}`,
      providesTags: (_result, _error, facturaId) => [{ type: 'Factura', id: facturaId }],
    }),

    solicitarFactura: builder.mutation<FacturaResult, SolicitarFacturaRequest>({
      query: (request) => ({
        url: '/facturacion/solicitar',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Order', { type: 'Factura', id: 'LIST' }],
    }),

    timbrarFactura: builder.mutation<TimbrarFacturaResult, number>({
      query: (facturaId) => ({
        url: `/facturacion/timbrar/${facturaId}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, facturaId) => [
        'Order',
        { type: 'Factura', id: facturaId },
        { type: 'Factura', id: 'LIST' },
        'StampBalance',
      ],
    }),

    regenerarFactura: builder.mutation<FacturaResult, { facturaId: number; request: SolicitarFacturaRequest }>({
      query: ({ facturaId, request }) => ({
        url: `/facturacion/regenerar/${facturaId}`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Order', { type: 'Factura', id: 'LIST' }],
    }),

    regenerarFacturaSinOrden: builder.mutation<FacturaResult, { facturaId: number; request: CrearFacturaSinOrdenRequest }>({
      query: ({ facturaId, request }) => ({
        url: `/facturacion/regenerar-sin-orden/${facturaId}`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [{ type: 'Factura', id: 'LIST' }],
    }),

    crearFacturaSinOrden: builder.mutation<FacturaResult, CrearFacturaSinOrdenRequest>({
      query: (request) => ({
        url: '/facturacion/crear-sin-orden',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [{ type: 'Factura', id: 'LIST' }],
    }),

    cancelarFactura: builder.mutation<CancelarFacturaResult, { facturaId: number; data: CancelarFacturaRequest }>({
      query: ({ facturaId, data }) => ({
        url: `/facturacion/cancelar/${facturaId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { facturaId }) => [
        'Order',
        { type: 'Factura', id: facturaId },
        { type: 'Factura', id: 'LIST' },
      ],
    }),

    /** Vuelve a preguntar al SAT cómo va una cancelación. */
    sincronizarCancelacion: builder.mutation<CancelarFacturaResult, number>({
      query: (facturaId) => ({
        url: `/facturacion/cancelar/${facturaId}/sincronizar`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, facturaId) => [
        'Order',
        { type: 'Factura', id: facturaId },
        { type: 'Factura', id: 'LIST' },
      ],
    }),

    /**
     * Motivo 04: factura a nombre del cliente un ticket que ya está en una global timbrada
     * (la global se reexpide sin él y la anterior se cancela).
     */
    facturarTicketDeGlobal: builder.mutation<FacturaResult, SolicitarFacturaRequest>({
      query: (request) => ({
        url: '/facturacion/global/facturar-ticket',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Order', { type: 'Factura', id: 'LIST' }, 'StampBalance'],
    }),

    /** Saca ya de la global los tickets que sus clientes facturaron a su nombre (motivo 04). */
    reexpedirGlobal: builder.mutation<ReexpedicionResultado, number>({
      query: (facturaId) => ({
        url: `/facturacion/global/${facturaId}/reexpedir`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, facturaId) => [
        'Order',
        { type: 'Factura', id: facturaId },
        { type: 'Factura', id: 'LIST' },
        'StampBalance',
      ],
    }),

    /** Los tickets que ampara una factura, con la referencia de la terminal. */
    getTicketsDeFactura: builder.query<FacturaTicket[], number>({
      query: (facturaId) => `/facturacion/facturas/${facturaId}/tickets`,
      providesTags: (_result, _error, facturaId) => [{ type: 'Factura', id: facturaId }],
    }),

    deleteFactura: builder.mutation<{ message: string }, number>({
      query: (facturaId) => ({
        url: `/facturacion/${facturaId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, facturaId) => [
        'Order',
        { type: 'Factura', id: facturaId },
        { type: 'Factura', id: 'LIST' },
      ],
    }),

    getFacturaPdf: builder.query<Blob, number>({
      query: (facturaId) => ({
        url: `/facturacion/pdf/${facturaId}`,
        responseHandler: (response) => response.blob(),
      }),
    }),

    getFacturaXml: builder.query<Blob, number>({
      query: (facturaId) => ({
        url: `/facturacion/xml/${facturaId}`,
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ============================================================================
    // Restaurant Facturacion Config
    // ============================================================================

    getFacturacionConfig: builder.query<RestaurantFacturacionConfig | null, void>({
      query: () => '/facturacion/config',
      providesTags: ['FacturacionConfig'],
    }),

    createFacturacionConfig: builder.mutation<RestaurantFacturacionConfig, CreateFacturacionConfigRequest>({
      query: (newConfig) => ({
        url: '/facturacion/config',
        method: 'POST',
        body: newConfig,
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    updateFacturacionConfig: builder.mutation<RestaurantFacturacionConfig, UpdateFacturacionConfigRequest>({
      query: (updatedConfig) => ({
        url: '/facturacion/config',
        method: 'PUT',
        body: updatedConfig,
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    activateFacturacion: builder.mutation<FacturacionActivationResult, ActivateFacturacionRequest>({
      query: (activationData) => ({
        url: '/facturacion/config/activate',
        method: 'POST',
        body: activationData,
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    deactivateFacturacion: builder.mutation<RestaurantFacturacionConfig, void>({
      query: () => ({
        url: '/facturacion/config/deactivate',
        method: 'POST',
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    toggleAutofactura: builder.mutation<RestaurantFacturacionConfig, { enabled: boolean }>({
      query: (body) => ({
        url: '/facturacion/config/autofactura-toggle',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    // ============================================================================
    // Certificados (CSD)
    // ============================================================================

    getCertificados: builder.query<CertificadoInfo[], void>({
      query: () => '/facturacion/certificados',
      providesTags: ['FacturacionConfig'],
    }),

    subirCertificado: builder.mutation<SubirCertificadoResult, SubirCertificadoRequest>({
      query: (certificado) => ({
        url: '/facturacion/certificados',
        method: 'POST',
        body: certificado,
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    activarCertificado: builder.mutation<ActivarCertificadoResult, number>({
      query: (id) => ({
        url: `/facturacion/certificados/${id}/activar`,
        method: 'POST',
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    revocarCertificado: builder.mutation<RevocarCertificadoResult, number>({
      query: (id) => ({
        url: `/facturacion/certificados/${id}/revocar`,
        method: 'POST',
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    eliminarCertificado: builder.mutation<void, number>({
      query: (id) => ({
        url: `/facturacion/certificados/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FacturacionConfig'],
    }),

    // ============================================================================
    // SAT Catalogos
    // ============================================================================

    getUsosCFDI: builder.query<CatalogoUsoCFDI[], void>({
      query: () => '/facturacion/catalogos/usos-cfdi',
      providesTags: ['CatalogoSAT'],
    }),

    getRegimenesFiscales: builder.query<CatalogoRegimenFiscal[], void>({
      query: () => '/facturacion/catalogos/regimenes-fiscales',
      providesTags: ['CatalogoSAT'],
    }),

    getFormasPago: builder.query<CatalogoFormaPago[], void>({
      query: () => '/facturacion/catalogos/formas-pago',
      providesTags: ['CatalogoSAT'],
    }),

    getMetodosPago: builder.query<CatalogoMetodoPago[], void>({
      query: () => '/facturacion/catalogos/metodos-pago',
      providesTags: ['CatalogoSAT'],
    }),

    searchProductosServicios: builder.query<CatalogoProductoServicio[], string>({
      query: (search) => `/facturacion/catalogos/productos-servicios?search=${encodeURIComponent(search)}`,
      providesTags: ['CatalogoSAT'],
    }),

    getUnidadesMedida: builder.query<CatalogoUnidadMedida[], void>({
      query: () => '/facturacion/catalogos/unidades-medida',
      providesTags: ['CatalogoSAT'],
    }),

    // ============================================================================
    // Email
    // ============================================================================

    enviarFacturaEmail: builder.mutation<EnviarFacturaEmailResult, { facturaId: number; request: EnviarFacturaEmailRequest }>({
      query: ({ facturaId, request }) => ({
        url: `/facturacion/facturas/${facturaId}/enviar-email`,
        method: 'POST',
        body: {
          ...request,
          originUrl: window.location.origin, // Include origin URL for environment detection
        },
      }),
    }),

    // ============================================================================
    // Email Configuration
    // ============================================================================

    getEmailConfig: builder.query<RestaurantEmailConfig | null, void>({
      query: () => '/facturacion/email-config',
      providesTags: ['EmailConfig'],
    }),

    createEmailConfig: builder.mutation<RestaurantEmailConfig, CreateEmailConfigRequest>({
      query: (config) => ({
        url: '/facturacion/email-config',
        method: 'POST',
        body: config,
      }),
      invalidatesTags: ['EmailConfig'],
    }),

    updateEmailConfig: builder.mutation<RestaurantEmailConfig, UpdateEmailConfigRequest>({
      query: (config) => ({
        url: '/facturacion/email-config',
        method: 'PUT',
        body: config,
      }),
      invalidatesTags: ['EmailConfig'],
    }),

    testEmailConfig: builder.mutation<TestEmailConfigResult, TestEmailConfigRequest>({
      query: (request) => ({
        url: '/facturacion/email-config/test',
        method: 'POST',
        body: request,
      }),
    }),

    // ============================================================================
    // Facturación Masiva
    // ============================================================================

    getOrdenesSinFacturar: builder.query<OrdenesSinFacturarResult, OrdenesSinFacturarFilter>({
      query: (filter) => {
        const params = new URLSearchParams();
        if (filter.fechaInicio) params.append('fechaInicio', filter.fechaInicio);
        if (filter.fechaFin) params.append('fechaFin', filter.fechaFin);
        if (filter.formaPago) params.append('formaPago', filter.formaPago);

        const queryString = params.toString();
        return `/facturacion/ordenes-sin-facturar${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Order', { type: 'Factura', id: 'LIST' }],
    }),

    facturarOrdenesMasivo: builder.mutation<FacturarOrdenesMasivoResult, FacturarOrdenesMasivoRequest>({
      query: (request) => ({
        url: '/facturacion/facturar-masivo',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Order', { type: 'Factura', id: 'LIST' }],
    }),

    timbrarFacturasMasivo: builder.mutation<TimbrarFacturasMasivoResult, TimbrarFacturasMasivoRequest>({
      query: (request) => ({
        url: '/facturacion/timbrar-masivo',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Order', { type: 'Factura', id: 'LIST' }, 'StampBalance'],
    }),

    regenerarFacturasMasivo: builder.mutation<RegenerarFacturasMasivoResult, RegenerarFacturasMasivoRequest>({
      query: (request) => ({
        url: '/facturacion/regenerar-masivo',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Order', { type: 'Factura', id: 'LIST' }],
    }),

    // ============================================================================
    // SIFEI Environment (Soporte)
    // ============================================================================

    getSifeiEnvironment: builder.query<SifeiEnvironment, void>({
      query: () => '/facturacion/sifei-environment',
      providesTags: ['SifeiEnvironment'],
    }),

    updateSifeiEnvironment: builder.mutation<SifeiEnvironment, { environment: string }>({
      query: (request) => ({
        url: '/facturacion/sifei-environment',
        method: 'PATCH',
        body: request,
      }),
      invalidatesTags: ['SifeiEnvironment'],
    }),

    // ============================================================================
    // Timbres Compartidos
    // ============================================================================

    getTimbresCompartidos: builder.query<TimbresCompartidos, void>({
      query: () => '/facturacion/timbres-compartidos',
      providesTags: ['TimbresCompartidos'],
    }),

    // ============================================================================
    // Búsqueda y Vinculación por RFC
    // ============================================================================

    buscarPorRfc: builder.query<OrganizacionByRfc, string>({
      query: (rfc) => `/facturacion/buscar-por-rfc/${rfc}`,
    }),

    vincularPorRfc: builder.mutation<VincularPorRfcResult, VincularPorRfcRequest>({
      query: (body) => ({
        url: '/facturacion/vincular-por-rfc',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FacturacionConfig', 'TimbresCompartidos'],
    }),

    resyncTimbres: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/facturacion/resync-timbres',
        method: 'POST',
      }),
      invalidatesTags: ['TimbresCompartidos'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  // Clientes Fiscales hooks
  useGetClientesFiscalesQuery,
  useGetClienteFiscalByRFCQuery,
  useGetClienteFiscalByIdQuery,
  useCreateClienteFiscalMutation,
  useUpdateClienteFiscalMutation,
  useDeleteClienteFiscalMutation,
  useRefrescarClientesFiscalesMutation,
  useImportClientesFiscalesMutation,
  useParseQRClienteMutation,

  // Productos Facturacion hooks
  useGetProductosFacturacionQuery,
  useGetProductoFacturacionByProductIdQuery,
  useCreateProductoFacturacionMutation,
  useUpdateProductoFacturacionMutation,
  useDeleteProductoFacturacionMutation,
  useSyncProductoFacturadorMutation,

  // Facturacion hooks
  useGetFacturasQuery,
  useGetFacturaDetalleQuery,
  useSolicitarFacturaMutation,
  useTimbrarFacturaMutation,
  useRegenerarFacturaMutation,
  useRegenerarFacturaSinOrdenMutation,
  useCrearFacturaSinOrdenMutation,
  useCancelarFacturaMutation,
  useSincronizarCancelacionMutation,
  useFacturarTicketDeGlobalMutation,
  useReexpedirGlobalMutation,
  useLazyGetTicketsDeFacturaQuery,
  useDeleteFacturaMutation,
  useGetFacturaPdfQuery,
  useGetFacturaXmlQuery,

  // Restaurant Facturacion Config hooks
  useGetFacturacionConfigQuery,
  useCreateFacturacionConfigMutation,
  useUpdateFacturacionConfigMutation,
  useActivateFacturacionMutation,
  useDeactivateFacturacionMutation,
  useToggleAutofacturaMutation,

  // Certificados (CSD) hooks
  useGetCertificadosQuery,
  useSubirCertificadoMutation,
  useActivarCertificadoMutation,
  useRevocarCertificadoMutation,
  useEliminarCertificadoMutation,

  // SAT Catalogos hooks
  useGetUsosCFDIQuery,
  useGetRegimenesFiscalesQuery,
  useGetFormasPagoQuery,
  useGetMetodosPagoQuery,
  useSearchProductosServiciosQuery,
  useGetUnidadesMedidaQuery,

  // Email hooks
  useEnviarFacturaEmailMutation,

  // Email Config hooks
  useGetEmailConfigQuery,
  useCreateEmailConfigMutation,
  useUpdateEmailConfigMutation,
  useTestEmailConfigMutation,

  // Facturación Masiva hooks
  useGetOrdenesSinFacturarQuery,
  useFacturarOrdenesMasivoMutation,
  useTimbrarFacturasMasivoMutation,
  useRegenerarFacturasMasivoMutation,

  // SIFEI Environment (Soporte) hooks
  useGetSifeiEnvironmentQuery,
  useUpdateSifeiEnvironmentMutation,

  // Timbres Compartidos hooks
  useGetTimbresCompartidosQuery,

  // Búsqueda y Vinculación por RFC hooks
  useLazyBuscarPorRfcQuery,
  useVincularPorRfcMutation,

  // Resync timbres hook
  useResyncTimbresMutation,
} = facturacionApi;

export default facturacionApi;
